<script setup>
  import {computed, ref} from "vue";

  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import ComponentFakeInput from "@components/Inputs/FakeInput.vue";
  import ComponentInput from "@components/Inputs/Input.vue";
  import ComponentSelect from "@components/Selects/Select.vue";
  import {usePage} from "@inertiajs/vue3";
  import {status} from "@pages/Records/Components/Sections/UserBar/enums.js";
  import {MedButton} from "@pharma4u/medicheck-components";
  import {cn} from "@pharma4u/medicheck-components/utils";

  defineOptions({
    name: "RecordComponentSectionUserBar",
  });

  const props = defineProps({
    record: {
      type: Object,
      required: true,
    },
    isEditable: {
      type: Boolean,
      required: true,
    },
    changeValue: {
      type: Function,
      required: true,
    },
    saveRecord: {
      type: Function,
      required: true,
    },
  });

  const page = usePage();

  const showForm = ref(false);
  const refWarningDialog = ref(null);

  const date = computed(() => page.props.record.updated_at);

  const toggleUserForm = () => {
    showForm.value = !showForm.value;
  };

  const handleStatusChange = (newStatus) => {
    props.changeValue(newStatus, "userstatus");

    if (newStatus !== "pharmacist") {
      refWarningDialog.value.open();
    }
  };

  const confirmStatusChange = () => {
    refWarningDialog.value.close();

    props.saveRecord();
  };

  const saveUserBar = () => {
    showForm.value = false;
    props.saveRecord();
  };
</script>

<template>
  <div
    :class="cn('flex min-h-18 items-center gap-4 bg-white p-6 shadow-md', {'hover:bg-gray-100': !showForm})"
    :data-test="getTestId('user-bar')"
  >
    <MedButton
      aria-label="Bearbeiterdaten bearbeiten"
      variant="primary"
      icon="person"
      size="sm"
      rounded="full"
      :data-test="getTestId('user-bar-edit-button')"
      @click="toggleUserForm"
    />

    <div class="flex w-full flex-col gap-4">
      <div
        v-if="!showForm"
        role="button"
        tabindex="0"
        class="cursor-pointer"
        @click="toggleUserForm"
        @keydown.enter="toggleUserForm"
        @keydown.space.prevent="toggleUserForm"
      >
        <div>
          <span class="text-md black-800 font-medium">Bearbeiter</span>

          <br />

          <span class="text-xs text-gray-700">
            {{ record.username }},
            <span v-if="status[record.userstatus]">{{ status[record.userstatus] }},</span>
            {{ date }}
          </span>
        </div>
      </div>

      <div v-else>
        <h5 class="px-0.5 py-4 text-2xl">Bearbeiter</h5>

        <div class="ml-1 grid grid-cols-1 gap-8 sm:grid-cols-2 md:grid-cols-3">
          <ComponentInput
            ref="user"
            name="user"
            autocomplete="off"
            label="Bearbeiter:"
            :model-value="record.username"
            :disabled="!isEditable"
            :test-id="getTestId('user-bar-username')"
            @input="changeValue($event, 'username')"
          />

          <ComponentSelect
            id="userstatus"
            ref="userstatus"
            name="userstatus"
            :model-value="record.userstatus"
            :options="status"
            label="Beruf / Status:"
            :disabled="!isEditable"
            :test-id="getTestId('user-bar-userstatus')"
            @change="handleStatusChange($event)"
          />

          <ComponentFakeInput
            label="Geändert:"
            :value="date"
            :disabled="!isEditable"
            :disabled-text="!isEditable"
            :test-id="getTestId('user-bar-date')"
          />
        </div>

        <ComponentDialog
          ref="refWarningDialog"
          dialog-title="Warnhinweis"
          dialog-type="information"
          :test-id="getTestId('user-bar-warn')"
          @dialog-closed="confirmStatusChange"
        >
          <template #content>
            <span class="info">CAVE:</span>
            Die Bewertung der Analyse und die Beratung im Rahmen eines Medikationsmanagements müssen durch einen
            Apotheker der Apotheke erfolgen (eine Unterstützung durch weiteres pharmazeutisches Personal ist allerdings
            erlaubt).
          </template>
        </ComponentDialog>
      </div>

      <template v-if="showForm && isEditable">
        <div class="flex justify-end gap-4">
          <MedButton :data-test="getTestId('user-bar-cancel-button')" @click="toggleUserForm">Abbrechen</MedButton>

          <MedButton
            class="w-fit"
            variant="primary"
            :data-test="getTestId('user-bar-save-button')"
            @click="saveUserBar"
          >
            Speichern
          </MedButton>
        </div>
      </template>
    </div>
  </div>
</template>
